import { Fragment, RefObject } from 'react';

import { TranslationFunction, TranslationPack } from '../i18n';

export const windowIsAvailable = typeof window !== 'undefined';
export const navigatorIsAvailable = typeof navigator !== 'undefined';
export const documentIsAvailable = typeof document !== 'undefined';

/** @returns Debounced function that can be called only once in given timeout */
export function debounce<F extends (...args: any[]) => any>(
  this: any,
  func: F,
  waitTime: number
): (...args: Parameters<F>) => void {
  let timeout: any;
  return (...args) => {
    const waitFunc = () => {
      timeout = null;
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(waitFunc, waitTime);
  };
}

/** Check for an existence of prop */
export function hasProp<T extends object, P extends PropertyKey>(
  obj: T,
  prop: P
): obj is T & { [key in P]: unknown } {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

// Add some context...
export function bindAll<T extends object, K extends keyof T>(obj: T, names: K[]): void {
  names.forEach(fn => {
    obj[fn] = (obj[fn] as unknown as Function).bind(obj);
  });
}

// Capitalize
export function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Generate unique id for elements */
export function createUID(): string {
  return `_${Math.random().toString(36).substr(2, 9)}`;
}

/** Returns array with numbers from given range */
export function range(start: number, end: number): number[] {
  return Array(end - start + 1)
    .fill(0)
    .map((_: any, idx: number) => start + idx);
}

/** Return an array of strings and regex replaced components */
export function replaceMatchWithElement(
  str: string,
  re: RegExp,
  render: (match: string) => JSX.Element
) {
  const arr: (string | JSX.Element)[] = [];

  // Only loop for stateful regexes.
  if (re.global || re.sticky) {
    let currIdx = 0;
    let match: RegExpExecArray | null;

    // eslint-disable-next-line no-cond-assign
    while ((match = re.exec(str))) {
      arr.push(str.slice(currIdx, match.index), render(match[0]));

      currIdx = match.index + match[0].length;
    }

    arr.push(str.slice(currIdx));
  } else {
    const match = re.exec(str);
    if (!match) arr.push(str);
    else {
      arr.push(
        str.slice(0, match.index),
        render(match[0]),
        str.slice(match.index + match[0].length)
      );
    }
  }

  return arr.flatMap((item, idx) => {
    if (!item) return [];

    return <Fragment key={idx}>{item}</Fragment>;
  });
}

/** Get scrollbar width */
export function getScrollbarWidth(): number {
  const scrollDiv = document.createElement('div');
  const divSize = '50px';
  let size = 0;

  scrollDiv.style.position = 'absolute';
  scrollDiv.style.top = `-${divSize}`;
  scrollDiv.style.width = divSize;
  scrollDiv.style.height = divSize;
  scrollDiv.style.overflow = 'scroll';

  document.body.appendChild(scrollDiv);
  size = scrollDiv.offsetWidth - scrollDiv.clientWidth;
  document.body.removeChild(scrollDiv);

  return size;
}

/** Test element for overflow */
export function testElForOverflow(el: Element) {
  return [el.scrollWidth > el.clientWidth, el.scrollHeight > el.clientHeight];
}

/** Used mostly to trigger css transition after DOM append */
export function reflow(node?: HTMLElement | null): number {
  node = node ?? document.body;
  return node.offsetWidth;
}

export function loadScript(
  src: string,
  onLoad: (this: HTMLScriptElement, ev: Event) => any,
  onError: (this: HTMLScriptElement, ev: ErrorEvent) => any
) {
  const scriptElem = document.createElement('script');
  scriptElem.src = src;

  scriptElem.addEventListener('load', onLoad);
  scriptElem.addEventListener('error', onError);
  document.head.appendChild(scriptElem);
}

export function deepGet(obj: object, keys: PropertyKey[]): any {
  let rtn: any = obj;

  const found = keys.every(key => {
    if (typeof rtn === 'object' && rtn) {
      rtn = rtn[key];
      return true;
    }
    return false;
  });

  if (found) return rtn;

  return undefined;
}

/** Compares two objects by comparing each key value regardless of order. */
export function deepObjectComparison(valueA: any, valueB: any): boolean {
  // If the values are strictly equal, return true
  if (valueA === valueB) return true;

  // If the values are not objects and are not equal, return false
  if (
    typeof valueA !== 'object' ||
    typeof valueB !== 'object' ||
    valueA === null ||
    valueB === null
  )
    return false;

  const keysA = Object.keys(valueA);
  const keysB = Object.keys(valueB);

  // If the objects have different numbers of keys, return false
  if (keysA.length !== keysB.length) return false;

  for (let i = 0; i < keysA.length; i += 1) {
    const key = keysA[i];
    // If there is not a 1:1 mapping of keys between the objects, return false
    if (!keysB.includes(key)) return false;

    if (typeof valueA[key] === 'function' || typeof valueB[key] === 'function') {
      // If the value of a given key is a function, and the functions are not equivalent between the objects, return false
      if (valueA[key].toString() !== valueB[key].toString()) return false;
      // If all checks have passed thus far, compare the next set of nested values
    } else if (!deepObjectComparison(valueA[key], valueB[key])) return false;
  }

  // If all checks have passed, the objects are deeply equal
  return true;
}

export function defineSmartGetter(
  obj: Record<string, any>,
  key: string,
  valueFn: (...args: any[]) => any
) {
  Object.defineProperty(obj, key, {
    get: () => {
      delete obj[key];
      obj[key] = valueFn();
      return obj[key];
    },
    enumerable: true,
    configurable: true
  });

  return obj;
}

export function tryCatch<T>(tryFn: () => T): T | undefined;
export function tryCatch<T, C>(tryFn: () => T, catchFn: (err: unknown) => C): T | C;
export function tryCatch<T, C, F>(
  tryFn: () => T,
  catchFn: ((err: unknown) => C) | undefined,
  finallyFn: () => F
): F;
export function tryCatch<T, C, F>(
  tryFn: () => T,
  catchFn?: (err: unknown) => C,
  finallyFn?: () => F
): T | C | F | undefined {
  try {
    return tryFn();
  } catch (err) {
    if (catchFn) return catchFn(err);
  } finally {
    // eslint-disable-next-line no-unsafe-finally
    if (finallyFn) return finallyFn();
  }
}

/**
 * Returns normalized list of elements, ie. it de-reference the element from the Ref if needed.
 * @param els a list of elements or refs to normalize.
 * @returns a new list of the same length with the de-referenced elements.
 */
export const normalizeElements = <T extends Element = Element>(
  els: (T | null | RefObject<T | null>)[]
) => {
  return els.map((el: T | null | RefObject<T | null>) => {
    if (!el) return null;
    return el instanceof Element ? el : el.current;
  });
};

/**
 * Returns all valid focusable elements within a given ref.
 * @param focusablesRef The ref containing the element to search for focusable elements within.
 * @returns a list of valid focusable elements within the given ref.
 */
export const getFocusables = (focusablesRef: RefObject<HTMLElement>) => {
  if (!focusablesRef.current) return [];

  const selector = 'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';

  return ([...focusablesRef.current.querySelectorAll(selector)] as HTMLElement[]).filter(
    el => el instanceof HTMLElement && !el.hasAttribute('disabled')
  );
};

/**
 * Generator that returns triple of subsequent values in the given array.
 * @param array source array
 * @returns triple of previous, current and next value on every call
 */
export function* triple<T>(
  array: T[]
): Generator<{ prev?: T; current: T; next?: T }, undefined, unknown> {
  for (let i = 0; i < array.length; i += 1) {
    yield { prev: array[i - 1], current: array[i], next: array[i + 1] };
  }
  return undefined;
}

/**
 * Escape special characters in order to pass a string to the RegExp constructor.
 * @param s a string.
 * @returns a string with special characters escaped.
 */
export const escapeRegExp = (s: string): string => String(s).replace(/[\\^$*+?.()|[\]{}]/g, '\\$&');

/**
 * Generate a RegExp with a string parameter.
 * @param s The string to match.
 * @param type How to match.
 * @param flags Standard RegExp flags.
 * @returns A RegExp with the match pattern.
 */
export const createStringMatcher = (
  s: string,
  type: 'contains' | 'start' | 'boundary' = 'contains',
  flags: string = 'i'
): RegExp => {
  const escaped = escapeRegExp(s);
  let pattern: string = escaped;

  if (type === 'start') {
    pattern = `^${escaped}`;
  } else if (type === 'boundary') {
    pattern = `(?:^|\\s)${escaped}`;
  }

  return new RegExp(pattern, flags);
};

/**
 * Function generates string representation of the given list accordingly to localization rules of given locale.
 * @param list list of strings to format
 * @param t translation function used internally
 * @param locale locale tag to get the localization rules for
 * @param options formatting options
 * @returns
 */
export const formatListToLocaleString = (
  list: string[],
  t: TranslationFunction<TranslationPack>,
  locale: string,
  {
    count = list.length,
    separator
  }: {
    count?: number;
    separator?: string;
  } = {}
) => {
  const remainder = count - list.length;

  const toFormat =
    remainder > 0 ? [...list, t('more_count', [remainder], { count: remainder })] : list;

  if (separator || !('ListFormat' in Intl)) return toFormat.join(separator ?? ', ');

  return new Intl.ListFormat(locale, { style: 'long' }).format(toFormat);
};
